import { useState, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Send, Loader2 } from "lucide-react";

// Set VITE_CHAT_ENDPOINT in .env.local to point at your AI backend.
// Example: VITE_CHAT_ENDPOINT=http://blueflynas.tailcf98b3.ts.net:3055/api/chat
const CHAT_ENDPOINT = import.meta.env.VITE_CHAT_ENDPOINT ?? "";

interface Message {
  role: "user" | "assistant";
  content: string;
}

export function Chat() {
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-send ?q= param on arrival (from SpotSheet "Ask Blu" CTA)
  const sentInitial = useRef(false);
  useEffect(() => {
    const q = searchParams.get("q");
    if (q && q.trim() && !sentInitial.current) {
      sentInitial.current = true;
      setInput(q.trim());
    }
  }, [searchParams]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  async function send(text?: string) {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    if (!text) setInput("");

    const userMsg: Message = { role: "user", content };
    const next = [...messages, userMsg];
    setMessages(next);
    setLoading(true);

    if (!CHAT_ENDPOINT) {
      setMessages([
        ...next,
        {
          role: "assistant",
          content:
            "⚙️  No chat endpoint configured.\n\nAdd `VITE_CHAT_ENDPOINT=<your-url>` to `frontend/.env.local` and restart the dev server.",
        },
      ]);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(CHAT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: next,
          system:
            "You are Blu, an expert fly fishing guide for Southern Maine. Help anglers with rivers, hatches, gear, tactics, regulations, and conservation ethics. Be concise and practical. Never reveal exact GPS coordinates of sensitive wild trout locations.",
        }),
      });

      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);

      // Handle both streaming (text/event-stream) and JSON responses
      const ct = res.headers.get("content-type") ?? "";
      if (ct.includes("event-stream")) {
        const reader = res.body!.getReader();
        const dec = new TextDecoder();
        let buf = "";
        setMessages((m) => [...m, { role: "assistant", content: "" }]);
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() ?? "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const data = line.slice(6).trim();
            if (data === "[DONE]") break;
            try {
              const j = JSON.parse(data);
              const delta =
                j.choices?.[0]?.delta?.content ??
                j.delta?.text ??
                j.text ??
                "";
              if (delta) {
                setMessages((m) => {
                  const copy = [...m];
                  copy[copy.length - 1] = {
                    role: "assistant",
                    content: copy[copy.length - 1].content + delta,
                  };
                  return copy;
                });
              }
            } catch {
              /* ignore malformed chunks */
            }
          }
        }
      } else {
        const j = await res.json();
        const reply =
          j.message?.content ??
          j.choices?.[0]?.message?.content ??
          j.content ??
          j.text ??
          JSON.stringify(j);
        setMessages((m) => [...m, { role: "assistant", content: reply }]);
      }
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: `⚠️  Error: ${err instanceof Error ? err.message : String(err)}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        height: "calc(100dvh - 64px - env(safe-area-inset-bottom, 8px))",
        display: "flex",
        flexDirection: "column",
        background: "var(--surface-1)",
      }}
    >
      {/* Nav */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 16px",
          background: "rgba(17,17,17,0.94)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: "1px solid var(--surface-4)",
          flexShrink: 0,
        }}
      >
        <Link
          to="/"
          aria-label="Back"
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--surface-3)",
            color: "var(--text-secondary)",
            textDecoration: "none",
          }}
        >
          <ArrowLeft size={18} />
        </Link>
        <div style={{ flex: 1 }}>
          <h1
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: "var(--text-primary)",
              lineHeight: 1.2,
            }}
          >
            Blu · AI Guide
          </h1>
          <p style={{ fontSize: 11, color: "var(--pine-light)", marginTop: 1 }}>
            Southern Maine fly fishing
          </p>
        </div>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--pine)",
          }}
        >
          <BookOpen size={15} color="#4cd964" />
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 8px" }}>
        {messages.length === 0 && (
          <div
            style={{
              textAlign: "center",
              paddingTop: 48,
              color: "var(--text-muted)",
            }}
          >
            <div style={{ fontSize: 40, marginBottom: 12 }}>🎣</div>
            <p
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: "var(--text-secondary)",
                marginBottom: 6,
              }}
            >
              Ask Blu anything
            </p>
            <p style={{ fontSize: 13 }}>
              Rivers · Hatches · Flies · Conditions · Access
            </p>
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: m.role === "user" ? "flex-end" : "flex-start",
              marginBottom: 12,
            }}
          >
            <div
              style={{
                maxWidth: "82%",
                padding: "10px 14px",
                borderRadius: m.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                background:
                  m.role === "user" ? "var(--pine)" : "var(--surface-3)",
                color:
                  m.role === "user" ? "#ffffff" : "var(--text-primary)",
                fontSize: 14,
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
              }}
            >
              {m.content}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 12 }}>
            <div
              style={{
                padding: "10px 14px",
                borderRadius: "18px 18px 18px 4px",
                background: "var(--surface-3)",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Loader2 size={14} color="var(--pine-light)" className="animate-spin" />
              <span style={{ fontSize: 13, color: "var(--text-muted)" }}>Thinking…</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div
        style={{
          padding: "8px 16px 16px",
          borderTop: "1px solid var(--surface-4)",
          background: "var(--surface-1)",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "flex-end",
            background: "var(--surface-3)",
            borderRadius: 24,
            padding: "8px 8px 8px 16px",
          }}
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask about rivers, flies, conditions…"
            rows={1}
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "var(--text-primary)",
              fontSize: 15,
              resize: "none",
              fontFamily: "inherit",
              lineHeight: 1.5,
              maxHeight: 120,
              overflowY: "auto",
            }}
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || loading}
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "none",
              background: input.trim() && !loading ? "var(--pine)" : "var(--surface-4)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: input.trim() && !loading ? "pointer" : "default",
              flexShrink: 0,
              transition: "background 0.15s",
            }}
            aria-label="Send"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
